import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/shadcn/button';
import { Input } from '@/components/shadcn/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/shadcn/form';
import { Alert, AlertDescription } from '@/components/shadcn/alert';
import { requestPasswordReset } from '@/services/passwordReset';

const forgotSchema = z.object({
  email: z.string().min(1, 'Email wajib diisi').email('Format email tidak valid'),
});

type ForgotFormValues = z.infer<typeof forgotSchema>;

export function ForgotPasswordForm() {
  const navigate = useNavigate();
  const [apiError, setApiError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<ForgotFormValues>({
    resolver: zodResolver(forgotSchema),
    defaultValues: { email: '' },
  });

  const onSubmit = async (values: ForgotFormValues) => {
    setApiError(null);
    setIsSubmitting(true);
    try {
      await requestPasswordReset(values.email.trim().toLowerCase());
      const email = encodeURIComponent(values.email.trim().toLowerCase());
      navigate(`/check-email?context=reset&email=${email}`);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message =
          (error.response?.data as { message?: string })?.message ??
          'Terjadi kesalahan sistem, silakan coba lagi nanti';
        setApiError(message);
      } else if (error instanceof Error) {
        setApiError(error.message);
      } else {
        setApiError('Terjadi kesalahan sistem, silakan coba lagi nanti');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <h1 className="mb-1 text-xl font-bold text-foreground">Lupa Password</h1>
      <p className="mb-6 text-sm text-muted-foreground">
        Masukkan email terdaftar, kami kirimkan tautan reset.
      </p>
      {apiError && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{apiError}</AlertDescription>
        </Alert>
      )}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email terdaftar</FormLabel>
                <FormControl>
                  <Input placeholder="nama@email.com" type="email" autoComplete="email" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            type="submit"
            className="w-full bg-primary-500 hover:bg-primary-600"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Memproses...' : 'Kirim tautan reset'}
          </Button>
        </form>
      </Form>
    </div>
  );
}
